import { ErrorState } from '@/components/primitives/ErrorState'
import { LoadingState } from '@/components/primitives/LoadingState'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import {
  type Bucket,
  fetchDashAI,
  fetchDashAnalytics,
  fetchDashFinance,
  fetchDashScrapers,
  fetchDashSystem
} from '@/lib/dashboards'
import { Sparkline } from '@/pages/dashboard/components/Sparkline'
import { StatCard } from '@/pages/dashboard/components/StatCard'
import { useQuery } from '@tanstack/react-query'
import {
  Activity,
  Bot,
  Brain,
  CheckCircle2,
  Cpu,
  Database,
  DollarSign,
  Globe,
  HeartPulse,
  Hourglass,
  MessageSquare,
  Newspaper,
  ShoppingCart,
  Sparkles,
  Target,
  Trophy,
  Users,
  Wallet,
  XCircle,
  Zap
} from 'lucide-react'

function bucketToValues(b: Bucket[] | undefined): number[] {
  return (b ?? []).map((x) => x.v)
}

// =========================================================================
// Analytics
// =========================================================================

export function DashboardAnalytics() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['dash-analytics'],
    queryFn: fetchDashAnalytics,
    refetchInterval: 120_000
  })

  if (isLoading) return <LoadingState variant="tiles" rows={12} />
  if (error || !data)
    return <ErrorState error={error ?? 'No data'} onRetry={() => refetch()} />

  const { people, content, timeseries, top_creators, top_cities, funnel } = data
  const conv =
    funnel.signups_30d > 0
      ? (funnel.with_first_post / funnel.signups_30d) * 100
      : 0
  const ret =
    funnel.signups_30d > 0 ? (funnel.retained_7d / funnel.signups_30d) * 100 : 0

  return (
    <div className="space-y-4">
      {/* People */}
      <SectionHeader icon={<Users className="size-4" />} title="People" />
      <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
        <StatCard
          label="Total users"
          value={people.total}
          icon={<Users className="size-3" />}
        />
        <StatCard label="Active users" value={people.active} tone="success" />
        <StatCard
          label="Promoter bots"
          value={people.bots}
          icon={<Bot className="size-3" />}
        />
        <StatCard label="Signups today" value={people.today} />
        <StatCard label="Signups this week" value={people.this_week} />
        <StatCard label="Signups this month" value={people.this_month} />
      </div>

      {/* Content */}
      <SectionHeader icon={<Newspaper className="size-4" />} title="Content" />
      <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        <StatCard label="Posts today" value={content.posts_today} />
        <StatCard label="Stories today" value={content.stories_today} />
        <StatCard label="Quickies today" value={content.quickies_today} />
        <StatCard label="Comments today" value={content.comments_today} />
        <StatCard label="Posts (all-time)" value={content.posts_total} />
        <StatCard
          label="Posts visible"
          value={content.posts_visible}
          hint={`${content.posts_total - content.posts_visible} hidden`}
        />
        <StatCard
          label="Businesses listed"
          value={content.businesses_total}
          icon={<Globe className="size-3" />}
        />
      </div>

      {/* Funnel */}
      <SectionHeader
        icon={<Target className="size-4" />}
        title="Activation funnel (30d)"
      />
      <div className="grid gap-3 md:grid-cols-3">
        <StatCard
          label="Signups (30d)"
          value={funnel.signups_30d}
          tone="default"
        />
        <StatCard
          label="With first post"
          value={funnel.with_first_post}
          hint={`${conv.toFixed(1)}% of signups`}
          tone={conv > 30 ? 'success' : 'default'}
        />
        <StatCard
          label="Retained 7d"
          value={funnel.retained_7d}
          hint={`${ret.toFixed(1)}% of signups`}
          tone={ret > 20 ? 'success' : 'warning'}
        />
      </div>

      {/* Time series */}
      <SectionHeader
        icon={<Activity className="size-4" />}
        title="Trends (30d)"
      />
      <div className="grid gap-3 md:grid-cols-3">
        <TrendCard
          label="Signups"
          data={bucketToValues(timeseries.signups_30d)}
        />
        <TrendCard label="Posts" data={bucketToValues(timeseries.posts_30d)} />
        <TrendCard
          label="Comments"
          data={bucketToValues(timeseries.comments_30d)}
        />
      </div>

      {/* Top lists */}
      <div className="grid gap-3 lg:grid-cols-2">
        <Card className="rounded-2xl p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <Trophy className="size-4 text-primary" /> Top creators (30d)
          </div>
          <ul className="space-y-1 text-sm">
            {top_creators.length === 0 && (
              <li className="text-muted-foreground">No data yet.</li>
            )}
            {top_creators.map((c, i) => (
              <li key={c.id} className="flex items-center gap-2">
                <span className="w-6 text-right text-xs text-muted-foreground tabular-nums">
                  {i + 1}.
                </span>
                <span className="flex-1 truncate font-medium">
                  {c.username ?? `#${c.id}`}
                </span>
                <Badge variant="secondary" className="tabular-nums">
                  {c.posts_30d} posts
                </Badge>
              </li>
            ))}
          </ul>
        </Card>

        <Card className="rounded-2xl p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <Globe className="size-4 text-primary" /> Top cities
          </div>
          <ul className="space-y-1 text-sm">
            {top_cities.length === 0 && (
              <li className="text-muted-foreground">No location data yet.</li>
            )}
            {top_cities.map((c, i) => (
              <li key={c.city} className="flex items-center gap-2">
                <span className="w-6 text-right text-xs text-muted-foreground tabular-nums">
                  {i + 1}.
                </span>
                <span className="flex-1 truncate">{c.city}</span>
                <Badge variant="secondary" className="tabular-nums">
                  {c.users}
                </Badge>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  )
}

// =========================================================================
// AI
// =========================================================================

export function DashboardAI() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['dash-ai'],
    queryFn: fetchDashAI,
    refetchInterval: 120_000
  })

  if (isLoading) return <LoadingState variant="tiles" rows={8} />
  if (error || !data)
    return <ErrorState error={error ?? 'No data'} onRetry={() => refetch()} />

  const {
    cost,
    per_agent_today,
    ceo,
    cache_stats,
    bots,
    run_status_24h,
    cost_30d
  } = data
  const successRate =
    ceo.tool_calls_today > 0
      ? (ceo.tool_calls_success_today / ceo.tool_calls_today) * 100
      : 0

  return (
    <div className="space-y-4">
      <SectionHeader icon={<DollarSign className="size-4" />} title="AI cost" />
      <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-4">
        <StatCard label="Today" value={cost.today} prefix="$" decimals={2} />
        <StatCard label="This week" value={cost.week} prefix="$" decimals={2} />
        <StatCard
          label="This month"
          value={cost.month}
          prefix="$"
          decimals={2}
        />
        <StatCard
          label="All time"
          value={cost.all_time}
          prefix="$"
          decimals={2}
        />
      </div>

      <SectionHeader icon={<Brain className="size-4" />} title="AI CEO" />
      <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
        <StatCard
          label="Conversations (total)"
          value={ceo.conversations_total}
        />
        <StatCard label="Active today" value={ceo.conversations_active_today} />
        <StatCard label="Messages today" value={ceo.messages_today} />
        <StatCard label="Tool calls today" value={ceo.tool_calls_today} />
        <StatCard
          label="Tool success rate"
          value={successRate}
          decimals={1}
          suffix="%"
          tone={successRate > 90 ? 'success' : 'warning'}
        />
      </div>

      <SectionHeader
        icon={<Zap className="size-4" />}
        title="Prompt caching (7d)"
      />
      <div className="grid gap-3 md:grid-cols-4">
        <StatCard label="Input tokens" value={cache_stats.input} />
        <StatCard
          label="Cache reads"
          value={cache_stats.cache_read}
          tone="success"
        />
        <StatCard label="Cache writes" value={cache_stats.cache_write} />
        <StatCard
          label="Cache hit rate"
          value={cache_stats.hit_rate * 100}
          decimals={1}
          suffix="%"
          tone={cache_stats.hit_rate > 0.5 ? 'success' : 'warning'}
        />
      </div>

      <SectionHeader icon={<Bot className="size-4" />} title="Bot army" />
      <div className="grid gap-3 md:grid-cols-3">
        <StatCard label="Actions today" value={bots.actions_today} />
        <StatCard label="Actions last hour" value={bots.actions_last_hour} />
        <Card className="rounded-2xl p-4">
          <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Actions (30d)
          </div>
          <Sparkline data={bucketToValues(bots.actions_30d)} height={48} />
        </Card>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <Card className="rounded-2xl p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <Sparkles className="size-4 text-primary" /> Cost per agent today
          </div>
          {per_agent_today.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No agent runs yet today.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="py-1 text-left">Agent</th>
                  <th className="py-1 text-right">Runs</th>
                  <th className="py-1 text-right">Tokens</th>
                  <th className="py-1 text-right">Cost</th>
                </tr>
              </thead>
              <tbody>
                {per_agent_today.map((a) => (
                  <tr key={a.agent} className="border-t">
                    <td className="py-1.5 font-mono text-xs">{a.agent}</td>
                    <td className="py-1.5 text-right tabular-nums">{a.runs}</td>
                    <td className="py-1.5 text-right tabular-nums">
                      {(a.tokens_in + a.tokens_out).toLocaleString()}
                    </td>
                    <td className="py-1.5 text-right tabular-nums font-medium">
                      ${a.cost_usd.toFixed(4)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card className="rounded-2xl p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <Activity className="size-4 text-primary" /> Run status (24h)
          </div>
          {run_status_24h.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No runs in the last 24h.
            </p>
          ) : (
            <ul className="space-y-1 text-sm">
              {run_status_24h.map((r) => (
                <li key={r.status} className="flex items-center gap-2">
                  <Badge
                    variant={
                      r.status === 'error'
                        ? 'destructive'
                        : r.status === 'success'
                          ? 'default'
                          : 'secondary'
                    }
                  >
                    {r.status}
                  </Badge>
                  <span className="ml-auto tabular-nums">{r.count}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-4 border-t pt-3">
            <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Cost (30d)
            </div>
            <Sparkline data={bucketToValues(cost_30d)} height={36} />
          </div>
        </Card>
      </div>
    </div>
  )
}

// =========================================================================
// Scrapers
// =========================================================================

export function DashboardScrapers() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['dash-scrapers'],
    queryFn: fetchDashScrapers,
    refetchInterval: 120_000
  })

  if (isLoading) return <LoadingState variant="tiles" rows={6} />
  if (error || !data)
    return <ErrorState error={error ?? 'No data'} onRetry={() => refetch()} />

  const { sources, derived_totals } = data

  return (
    <div className="space-y-4">
      <SectionHeader
        icon={<Database className="size-4" />}
        title="Per-source health"
      />
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {sources.map((s) => (
          <Card key={s.key} className="rounded-2xl p-4">
            <div className="mb-2 flex items-center gap-2">
              <Database className="size-4 text-primary" />
              <span className="text-sm font-semibold">{s.label}</span>
              {!s.available ? (
                <Badge variant="outline" className="ml-auto">
                  unavailable
                </Badge>
              ) : (s.failed_24h ?? 0) > 0 ? (
                <Badge variant="destructive" className="ml-auto">
                  {s.failed_24h} failed (24h)
                </Badge>
              ) : (
                <Badge variant="default" className="ml-auto">
                  healthy
                </Badge>
              )}
            </div>
            {s.available && (
              <div className="grid grid-cols-2 gap-2 text-xs">
                <Stat label="Total" value={s.total ?? 0} />
                <Stat label="Today" value={s.today ?? 0} />
                <Stat label="Running now" value={s.running ?? 0} />
                <Stat
                  label="Failed (24h)"
                  value={s.failed_24h ?? 0}
                  tone={(s.failed_24h ?? 0) > 0 ? 'danger' : 'default'}
                />
              </div>
            )}
            {s.last_run_at && (
              <div className="mt-2 text-[11px] text-muted-foreground">
                Last run {new Date(s.last_run_at).toLocaleString()} ·{' '}
                {s.last_run_status}
              </div>
            )}
          </Card>
        ))}
      </div>

      <SectionHeader
        icon={<Sparkles className="size-4" />}
        title="Scraped totals"
      />
      <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        <StatCard
          label="Scraped businesses"
          value={derived_totals.scraped_businesses}
        />
        <StatCard
          label="Eventbrite organizers"
          value={derived_totals.eventbrite_organizers}
        />
        <StatCard
          label="Eventbrite venues"
          value={derived_totals.eventbrite_venues}
        />
        <StatCard
          label="Eventbrite events"
          value={derived_totals.eventbrite_events}
        />
        <StatCard
          label="Ticketmaster events"
          value={derived_totals.ticketmaster_events}
        />
        <StatCard
          label="HOD raw queue"
          value={derived_totals.hod_raw_queue}
          hint="Pending classification"
        />
        <StatCard
          label="HOD structured"
          value={derived_totals.hod_structured}
        />
      </div>
    </div>
  )
}

// =========================================================================
// Finance
// =========================================================================

export function DashboardFinance() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['dash-finance'],
    queryFn: fetchDashFinance,
    refetchInterval: 120_000
  })

  if (isLoading) return <LoadingState variant="tiles" rows={8} />
  if (error || !data)
    return <ErrorState error={error ?? 'No data'} onRetry={() => refetch()} />

  const {
    gmv,
    order_count,
    aov,
    wallet_totals,
    payouts,
    bookings,
    top_sellers,
    orders_30d
  } = data
  const pendingPayouts = payouts.by_status['pending'] ?? { count: 0, amount: 0 }
  const failedPayouts = payouts.by_status['failed'] ?? { count: 0, amount: 0 }
  const paidPayouts = payouts.by_status['paid'] ?? { count: 0, amount: 0 }

  return (
    <div className="space-y-4">
      <SectionHeader
        icon={<DollarSign className="size-4" />}
        title="Gross merchandise value"
      />
      <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
        <StatCard
          label="GMV today"
          value={gmv.today}
          prefix="$"
          decimals={2}
          tone="success"
        />
        <StatCard
          label="GMV this week"
          value={gmv.week}
          prefix="$"
          decimals={2}
        />
        <StatCard
          label="GMV this month"
          value={gmv.month}
          prefix="$"
          decimals={2}
        />
      </div>

      <SectionHeader
        icon={<ShoppingCart className="size-4" />}
        title="Orders"
      />
      <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
        <StatCard label="Today" value={order_count.today} />
        <StatCard label="This week" value={order_count.week} />
        <StatCard label="This month" value={order_count.month} />
        <StatCard label="All time" value={order_count.all_time} />
        <StatCard
          label="Average order value"
          value={aov}
          prefix="$"
          decimals={2}
        />
      </div>

      <SectionHeader
        icon={<Wallet className="size-4" />}
        title="Wallets aggregate"
      />
      <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
        <StatCard label="Wallet users" value={wallet_totals.users} />
        <StatCard
          label="Available"
          value={wallet_totals.available}
          prefix="$"
          decimals={2}
        />
        <StatCard
          label="Pending"
          value={wallet_totals.pending}
          prefix="$"
          decimals={2}
          tone="warning"
        />
        <StatCard
          label="Total earned"
          value={wallet_totals.earned}
          prefix="$"
          decimals={2}
          tone="success"
        />
        <StatCard
          label="Total withdrawn"
          value={wallet_totals.withdrawn}
          prefix="$"
          decimals={2}
        />
      </div>

      <SectionHeader icon={<Hourglass className="size-4" />} title="Payouts" />
      <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
        <StatCard
          label="Pending"
          value={pendingPayouts.count}
          hint={`$${pendingPayouts.amount.toFixed(2)}`}
          tone="warning"
        />
        <StatCard
          label="Failed"
          value={failedPayouts.count}
          hint={`$${failedPayouts.amount.toFixed(2)}`}
          tone={failedPayouts.count > 0 ? 'danger' : 'default'}
        />
        <StatCard
          label="Paid"
          value={paidPayouts.count}
          hint={`$${paidPayouts.amount.toFixed(2)}`}
          tone="success"
        />
      </div>

      <SectionHeader
        icon={<MessageSquare className="size-4" />}
        title="Bookings"
      />
      <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-4">
        <StatCard
          label="Reservations today"
          value={bookings.reservations_today}
        />
        <StatCard
          label="Reservations week"
          value={bookings.reservations_week}
        />
        <StatCard
          label="Service bookings today"
          value={bookings.service_bookings_today}
        />
        <StatCard
          label="Space bookings today"
          value={bookings.space_bookings_today}
        />
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <Card className="rounded-2xl p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <Trophy className="size-4 text-primary" /> Top sellers (30d)
          </div>
          {top_sellers.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No paid orders in the last 30 days.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="py-1 text-left">Business</th>
                  <th className="py-1 text-right">Orders</th>
                  <th className="py-1 text-right">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {top_sellers.map((s) => (
                  <tr key={String(s.business_id)} className="border-t">
                    <td className="py-1.5">
                      {s.business_name ?? `#${s.business_id ?? '—'}`}
                    </td>
                    <td className="py-1.5 text-right tabular-nums">
                      {s.orders}
                    </td>
                    <td className="py-1.5 text-right tabular-nums font-medium">
                      ${s.revenue.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card className="rounded-2xl p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <Activity className="size-4 text-primary" /> Orders (30d)
          </div>
          <Sparkline data={bucketToValues(orders_30d)} height={120} />
        </Card>
      </div>
    </div>
  )
}

// =========================================================================
// System
// =========================================================================

export function DashboardSystem() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['dash-system'],
    queryFn: fetchDashSystem,
    refetchInterval: 30_000
  })

  if (isLoading) return <LoadingState variant="tiles" rows={6} />
  if (error || !data)
    return <ErrorState error={error ?? 'No data'} onRetry={() => refetch()} />

  return (
    <div className="space-y-4">
      <SectionHeader icon={<HeartPulse className="size-4" />} title="Health" />
      <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-4">
        <StatCard
          label="Celery queue depth"
          value={data.queue_depth}
          tone={data.queue_depth > 100 ? 'warning' : 'default'}
          icon={<Cpu className="size-3" />}
        />
        <Card className="rounded-2xl p-4">
          <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Redis
          </div>
          <div className="mt-1.5 flex items-center gap-2 text-base font-medium">
            {data.redis_ok ? (
              <>
                <CheckCircle2 className="size-4 text-emerald-600" /> Healthy
              </>
            ) : (
              <>
                <XCircle className="size-4 text-destructive" /> Unreachable
              </>
            )}
          </div>
          {data.redis_info?.used_memory_human && (
            <div className="mt-1 text-[11px] text-muted-foreground">
              Memory: {data.redis_info.used_memory_human}
              {data.redis_info.maxmemory_human
                ? ` / ${data.redis_info.maxmemory_human}`
                : ''}
            </div>
          )}
        </Card>
        <StatCard label="Audit log (24h)" value={data.audit_log_24h} />
        <Card className="rounded-2xl p-4">
          <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Last metrics refresh
          </div>
          <div className="mt-1.5 text-sm">
            {data.last_metrics_refresh_at
              ? new Date(data.last_metrics_refresh_at).toLocaleString()
              : '—'}
          </div>
        </Card>
      </div>

      <SectionHeader
        icon={<Cpu className="size-4" />}
        title="Celery beat schedule"
      />
      <Card className="rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5 text-left">Key</th>
              <th className="px-4 py-2.5 text-left">Task</th>
              <th className="px-4 py-2.5 text-right">Every</th>
            </tr>
          </thead>
          <tbody>
            {data.beat_schedule.map((b) => (
              <tr key={b.key} className="border-b last:border-0">
                <td className="px-4 py-2 font-mono text-xs">{b.key}</td>
                <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                  {b.task}
                </td>
                <td className="px-4 py-2 text-right text-xs tabular-nums">
                  {b.schedule_seconds ? formatSeconds(b.schedule_seconds) : '—'}
                </td>
              </tr>
            ))}
            {data.beat_schedule.length === 0 && (
              <tr>
                <td
                  colSpan={3}
                  className="p-4 text-center text-sm text-muted-foreground"
                >
                  No scheduled tasks.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  )
}

// =========================================================================
// Helpers
// =========================================================================

function SectionHeader({
  icon,
  title
}: { icon: React.ReactNode; title: string }) {
  return (
    <div className="mt-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {icon}
      {title}
    </div>
  )
}

function TrendCard({ label, data }: { label: string; data: number[] }) {
  const sum = data.reduce((s, v) => s + v, 0)
  return (
    <Card className="rounded-2xl p-4">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
        <span className="text-xs tabular-nums">
          {sum.toLocaleString()} total
        </span>
      </div>
      <Sparkline data={data} height={48} />
    </Card>
  )
}

function Stat({
  label,
  value,
  tone = 'default'
}: { label: string; value: number | string; tone?: 'default' | 'danger' }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div
        className={`mt-0.5 font-semibold tabular-nums ${tone === 'danger' ? 'text-destructive' : ''}`}
      >
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  )
}

function formatSeconds(s: number): string {
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.round(s / 60)}m`
  if (s < 86400) return `${Math.round(s / 3600)}h`
  return `${Math.round(s / 86400)}d`
}
